import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  userId: null,
  username: null,
  name: null,
  userType: null,
  roles: [],
  teams: [], // Array<TeamJwtInfoDto> z polami: id, isPreseasonDefined, teamName, tier, sportsClubId, sportsClubName, teamType, yearOfBirth
  selectedTeamId: null, // ID aktywnej druzyny coacha (wybieranej z dropdowna w navbarze)
  sportsClubId: null, // Tylko dla SPORTS_CLUB (dla COACH: null, derivowane z selectedTeam)
  sportsClubName: null, // Tylko dla SPORTS_CLUB (dla COACH: null, derivowane z selectedTeam)
  logoUrl: null,
  expressModeEnabled: false,
  expressModeThreshold: 7,
  isApproved: null,
  isAuthenticated: false
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserInfo: (state, action) => {
      // action.payload: { userId, username, name, userType, teams, sportsClubId, sportsClubName, logoUrl }
      state.userId = action.payload.userId;
      state.username = action.payload.username;
      state.name = action.payload.name;
      state.userType = action.payload.userType;
      state.roles = action.payload.roles || [];
      state.teams = action.payload.teams || [];
      state.selectedTeamId = action.payload.teams?.[0]?.id || null;
      state.sportsClubId = action.payload.sportsClubId || null;
      state.sportsClubName = action.payload.sportsClubName || null;
      state.logoUrl = action.payload.logoUrl || null;
      state.expressModeEnabled = action.payload.expressModeEnabled || false;
      state.expressModeThreshold = action.payload.expressModeThreshold ?? 7;
      state.isApproved = action.payload.isApproved ?? null;
      state.isAuthenticated = true;
    },
    clearUserInfo: (state) => {
      state.userId = null;
      state.username = null;
      state.name = null;
      state.userType = null;
      state.roles = [];
      state.teams = [];
      state.selectedTeamId = null;
      state.sportsClubId = null;
      state.sportsClubName = null;
      state.logoUrl = null;
      state.expressModeEnabled = false;
      state.expressModeThreshold = 7;
      state.isApproved = null;
      state.isAuthenticated = false;
    },
    setSelectedTeamId: (state, action) => {
      state.selectedTeamId = action.payload;
    },
    updateTeamPreseasonDefined: (state, action) => {
      // action.payload: { teamId: number, isPreseasonDefined: boolean }
      const { teamId, isPreseasonDefined } = action.payload;
      const teamIndex = state.teams.findIndex(t => t.id === teamId);
      if (teamIndex !== -1) {
        state.teams[teamIndex].isPreseasonDefined = isPreseasonDefined;
      }
    },
    addTeam: (state, action) => {
      // action.payload: { id, teamName, isPreseasonDefined, tier }
      state.teams.push(action.payload);
    },
    removeTeam: (state, action) => {
      // action.payload: teamId (number)
      state.teams = state.teams.filter(t => t.id !== action.payload);
    },
    setLogoUrl: (state, action) => {
      state.logoUrl = action.payload;
    },
  },
});

export const { setUserInfo, clearUserInfo, updateTeamPreseasonDefined, addTeam, removeTeam, setSelectedTeamId, setLogoUrl } = userSlice.actions;

// Selektory
export const selectUserId = (state) => state.user.userId;
export const selectUsername = (state) => state.user.username;
export const selectName = (state) => state.user.name;
export const selectUserType = (state) => state.user.userType;
export const selectTeams = (state) => state.user.teams;
export const selectSelectedTeamId = (state) => state.user.selectedTeamId;
// Pelny obiekt wybranej druzyny
export const selectSelectedTeam = (state) => {
  const { teams, selectedTeamId } = state.user;
  return teams?.find(t => t.id === selectedTeamId) || null;
};
// Selektor dla wstecznej kompatybilności - zwraca tablicę ID z obiektów teams
export const selectTeamIds = (state) => state.user.teams?.map(team => team.id) || [];
// Selektory derivowane z wybranej druzyny (dla COACH) lub z top-level (dla SPORTS_CLUB)
export const selectTeamName = (state) => {
  const selectedTeam = selectSelectedTeam(state);
  return selectedTeam?.teamName || null;
};
export const selectTier = (state) => {
  const selectedTeam = selectSelectedTeam(state);
  return selectedTeam?.tier || null;
};
export const selectTeamType = (state) => {
  const selectedTeam = selectSelectedTeam(state);
  return selectedTeam?.teamType || null;
};
export const selectYearOfBirth = (state) => {
  const selectedTeam = selectSelectedTeam(state);
  return selectedTeam?.yearOfBirth || null;
};
export const selectSportsClubId = (state) => {
  if (state.user.userType === 'COACH') {
    const selectedTeam = selectSelectedTeam(state);
    return selectedTeam?.sportsClubId || null;
  }
  return state.user.sportsClubId;
};
export const selectSportsClubName = (state) => {
  if (state.user.userType === 'COACH') {
    const selectedTeam = selectSelectedTeam(state);
    return selectedTeam?.sportsClubName || null;
  }
  return state.user.sportsClubName;
};
export const selectLogoUrl = (state) => state.user.logoUrl;
export const selectExpressModeEnabled = (state) => state.user.expressModeEnabled;
export const selectExpressModeThreshold = (state) => state.user.expressModeThreshold;
export const selectRoles = (state) => state.user.roles;
export const selectIsAdmin = (state) => (state.user.roles || []).includes('ADMIN');
export const selectIsApproved = (state) => state.user.isApproved;
export const selectIsAuthenticated = (state) => state.user.isAuthenticated;

export default userSlice.reducer;
